import { useEffect, useRef } from 'react';
import { subscribePortalResume, type PortalResumeDetail } from '../portal/portalResume';

export type UsePortalResumeOptions = {
  /**
   * Refetch if the tab was hidden at least this long (ms).
   * Shorter tab switches alone do not trigger the callback unless `staleTimeMs` also passes.
   */
  minHiddenMs: number;
  /**
   * Refetch if the last successful handler run is older than this (ms), same idea as React Query `staleTime`.
   */
  staleTimeMs: number;
};

/**
 * Runs `callback` after a portal resume only when either the tab was hidden long enough
 * or server-backed state is stale — same OR as TanStack Query–style focus refetch + min hidden.
 * Successful completion of `callback` (resolved promise) updates the stale clock; failures do not.
 */
export function usePortalResume(
  callback: (detail: PortalResumeDetail) => void | Promise<void>,
  options: UsePortalResumeOptions,
) {
  const { minHiddenMs, staleTimeMs } = options;
  const ref = useRef(callback);
  ref.current = callback;
  const lastSuccessAtRef = useRef(Date.now());

  useEffect(() => {
    return subscribePortalResume((detail) => {
      const passesHidden =
        detail.hiddenDurationMs === null || detail.hiddenDurationMs >= minHiddenMs;
      const now = Date.now();
      const passesStale = now - lastSuccessAtRef.current >= staleTimeMs;

      if (!passesHidden && !passesStale) {
        return;
      }

      void (async () => {
        try {
          await Promise.resolve(ref.current(detail));
          lastSuccessAtRef.current = Date.now();
        } catch {
          // failed refresh does not advance stale window
        }
      })();
    });
  }, [minHiddenMs, staleTimeMs]);
}

export type { PortalResumeDetail };
